import { z } from 'zod';
import type { ParseResult, SchemaIssue } from '../voyage/schemas.js';
import { decAdd, decCmp, decMul, decSub, formatDec } from './decimal.js';
import {
  optAmount,
  optEnum,
  optId,
  optLocalTime,
  optNote,
  optText,
  requiredIssues,
  resultOf,
  typed,
} from './fields.js';
import type { OperationKind } from './ledger.js';

/**
 * Transferler ve diğer hareketler (ortak belgesi §8, ekran 04; ADR 0117 §3): tank transferi
 * (gemi ROB'u değişmez), boşaltma, iade, yakıt değişimi (stok düşmez) ve diğer çıkış. Kaynak ve
 * hedef aynı tank olamaz (`SAME_TANK`). Yakıt uyumu, kapasite ve negatif bakiye uyarıdır
 * (`REVIEW`), yazmayı engellemez: gerçek olay kayda geçmeli (ADR 0117 §3). Saf modül.
 */

export const OPERATION_KINDS = [
  'transfer',
  'discharge',
  'return',
  'fuel_change',
  'other_out',
] as const satisfies readonly OperationKind[];

/** Hareketin ölçüm yöntemi (ekran 04 "Ölçüm Yöntemi"). */
export const OPERATION_METHODS = ['tank_gauging', 'flowmeter', 'bunker_meter', 'estimate'] as const;

/** Numaranın öneki, hareket türüne göre (`TR-0028`). */
export const OPERATION_PREFIX: Readonly<Record<OperationKind, string>> = {
  transfer: 'TR',
  discharge: 'DC',
  return: 'RT',
  fuel_change: 'FC',
  other_out: 'OT',
};

export const fuelOperationSchema = z.strictObject({
  kind: z.enum(OPERATION_KINDS, typed('INVALID_OPTION')),
  startedLocal: optLocalTime,
  endedLocal: optLocalTime,
  sourceTankId: optId,
  targetTankId: optId,
  fuelTypeId: optId,
  massMt: optAmount({ positive: true }),
  measurementMethod: optEnum(OPERATION_METHODS),
  responsibleId: optId,
  // Boşaltma: teslim alan ve liman
  receiver: optText(120),
  portName: optText(120),
  portCode: optText(12),
  // İade: ilgili alım
  bunkeringId: optId,
  /** İade ve diğer çıkışın nedeni. */
  reason: optText(300),
  // Yakıt değişimi: ekipman, önceki ve yeni yakıt, kaynak tanklar
  equipmentId: optId,
  previousFuelTypeId: optId,
  newFuelTypeId: optId,
  sourceTankIds: z
    .array(z.uuid('INVALID_ID'), typed('INVALID_LIST'))
    .max(10, 'TOO_LONG')
    .nullish()
    .transform((value) => value ?? []),
  voyageId: optId,
  description: optNote(500),
});
export type FuelOperationInput = z.output<typeof fuelOperationSchema>;

const COMMON_REQUIRED = ['startedLocal', 'endedLocal', 'responsibleId'] as const;
const REQUIRED: Readonly<Record<OperationKind, readonly (keyof FuelOperationInput & string)[]>> = {
  transfer: [
    ...COMMON_REQUIRED,
    'sourceTankId',
    'targetTankId',
    'fuelTypeId',
    'massMt',
    'measurementMethod',
  ],
  discharge: [...COMMON_REQUIRED, 'sourceTankId', 'fuelTypeId', 'massMt', 'receiver', 'portName'],
  return: [...COMMON_REQUIRED, 'bunkeringId', 'sourceTankId', 'fuelTypeId', 'massMt', 'reason'],
  fuel_change: [
    ...COMMON_REQUIRED,
    'equipmentId',
    'previousFuelTypeId',
    'newFuelTypeId',
    'sourceTankIds',
  ],
  other_out: [...COMMON_REQUIRED, 'sourceTankId', 'fuelTypeId', 'massMt', 'reason'],
};

export function parseFuelOperation(input: unknown): ParseResult<FuelOperationInput> {
  return resultOf(fuelOperationSchema.safeParse(input));
}

/** Gönderim kuralları: türün zorunluları, aynı tank yasağı, zaman sırası, değişimde iki yakıt farklı. */
export function operationSubmitIssues(value: FuelOperationInput): SchemaIssue[] {
  const issues = requiredIssues(value, REQUIRED[value.kind]);
  if (
    value.kind === 'transfer' &&
    value.sourceTankId !== null &&
    value.sourceTankId === value.targetTankId
  ) {
    issues.push({ path: 'targetTankId', code: 'SAME_TANK' });
  }
  if (value.startedLocal && value.endedLocal && value.endedLocal < value.startedLocal) {
    issues.push({ path: 'endedLocal', code: 'END_BEFORE_START' });
  }
  if (
    value.kind === 'fuel_change' &&
    value.previousFuelTypeId !== null &&
    value.previousFuelTypeId === value.newFuelTypeId
  ) {
    issues.push({ path: 'newFuelTypeId', code: 'SAME_FUEL' });
  }
  return issues;
}

/** Kaydın türüne göre anlamsız alanlar boşaltılır (tür değiştirilen taslak eski alanı taşımaz). */
export function normalizeOperation(value: FuelOperationInput): FuelOperationInput {
  const keep = new Set<string>([
    'kind',
    'startedLocal',
    'endedLocal',
    'responsibleId',
    'voyageId',
    'description',
    ...REQUIRED[value.kind],
    ...(value.kind === 'discharge' ? ['portCode'] : []),
    ...(value.kind === 'fuel_change' ? [] : ['measurementMethod']),
  ]);
  const out = { ...value } as Record<string, unknown>;
  for (const key of Object.keys(out)) {
    if (!keep.has(key)) out[key] = key === 'sourceTankIds' ? [] : null;
  }
  return out as FuelOperationInput;
}

/** Uyarılar (`REVIEW`): engel değil, kontrol ve dönem kapama blokajı (ADR 0117 §3, §10). */
export type OperationWarning =
  | { code: 'FUEL_NOT_ALLOWED_IN_TANK'; tankId: string }
  | { code: 'CAPACITY_EXCEEDED'; tankId: string }
  | { code: 'NEGATIVE_ROB'; tankId: string };

export interface TankForCheck {
  id: string;
  allowedFuelTypeIds: readonly string[];
  /** İşletme kapasitesi (m³). */
  operatingCapacityM3: string;
}

/**
 * Hareketin uyarıları: hedef tank yakıta izinli mi, hedefin tahmini hacmi (MT / yoğunluk)
 * işletme kapasitesini aşıyor mu, kaynak eksiye düşüyor mu. Bakiye ve yoğunluk defterden.
 */
export function operationWarnings(input: {
  kind: OperationKind;
  sourceTankId: string | null;
  targetTankId: string | null;
  fuelTypeId: string | null;
  massMt: string | null;
  tanks: readonly TankForCheck[];
  robByTank: Readonly<Record<string, string>>;
  density15ByTank: Readonly<Record<string, string | null>>;
}): OperationWarning[] {
  const warnings: OperationWarning[] = [];
  if (input.kind === 'fuel_change' || input.massMt === null) return warnings;
  const mass = input.massMt;
  const target = input.tanks.find((tank) => tank.id === input.targetTankId);
  if (input.kind === 'transfer' && target) {
    if (input.fuelTypeId !== null && !target.allowedFuelTypeIds.includes(input.fuelTypeId)) {
      warnings.push({ code: 'FUEL_NOT_ALLOWED_IN_TANK', tankId: target.id });
    }
    const density =
      input.density15ByTank[target.id] ?? input.density15ByTank[input.sourceTankId ?? ''];
    if (density) {
      const after = decAdd(input.robByTank[target.id] ?? '0', mass);
      const volume = formatDec(decMul(after, '1', 6));
      // Hacim = kütle / yoğunluk ≤ kapasite ⇔ kütle ≤ kapasite × yoğunluk
      if (decCmp(volume, decMul(target.operatingCapacityM3, density, 6)) > 0) {
        warnings.push({ code: 'CAPACITY_EXCEEDED', tankId: target.id });
      }
    }
  }
  if (input.sourceTankId !== null) {
    const after = decSub(input.robByTank[input.sourceTankId] ?? '0', mass);
    if (after.units < 0n) warnings.push({ code: 'NEGATIVE_ROB', tankId: input.sourceTankId });
  }
  return warnings;
}
